import { memo, useCallback, useMemo, useState } from 'react';
import { mergeClasses } from '@fluentui/react-components';
import shallow from 'zustand/shallow';
import { useEntry, useGridStore, useIsGridReadOnly } from '../state';
import { useInitialData } from '../../../hooks/useInitialData';
import { roundToStep } from '../utils/roundToStep';
import { bigZero } from '../../../math/math';
import { CommentPopover } from './CommentPopover';
import { FlagButton } from './FlagButton';
import { useRegisterTimeHours } from '../hooks/useRegisterTimeHours';
import { useTimeGridStyles } from '../useTimeGridStyles';
import { useDateColumnStyles } from '../hooks/useDateStyles';
import { useGridKeyboardNavigation } from '../hooks/useGridKeyboardNavigation';
import { resolveWorkTypeForApi } from '../utils/resolveWorkTypeForApi';

type HoursInputProps = {
    taskId: string;
    date: string;
    dates: string[];
    taskName: string;
    workType: string;
};

export const HoursInput = memo(function HoursInput({ taskId, date, dates, taskName, workType }: HoursInputProps) {
    const readOnly = useIsGridReadOnly();

    const {
        data: {
            configuration: { timeEntryRoundingInterval, allowCellComment, allowCellFlag, workWeek },
        },
    } = useInitialData();

    const { upsertEntry, periodDataId } = useGridStore(
        state => ({
            upsertEntry: state.upsertEntry,
            periodDataId: state.periodData?.id ?? '',
        }),
        shallow,
    );

    const entry = useEntry(taskId, date, workType);

    const [localValue, setLocalValue] = useState('');
    const [isEditing, setIsEditing] = useState(false);

    const { mutate: registerTimeHours } = useRegisterTimeHours();

    const styles = useTimeGridStyles();
    const singleDateArray = useMemo(() => [date], [date]);
    const resolvedWorkWeek = useMemo(() => workWeek ?? [], [workWeek]);
    const dateColumnStyles = useDateColumnStyles(singleDateArray, resolvedWorkWeek);

    const handleKeyDown = useGridKeyboardNavigation(taskId, date, dates, workType);

    const storeDisplayValue = useMemo(() => {
        // const fixed = entry.value.toFixed(2);
        // return fixed === '0.00' ? '' : fixed;
        const fixed = entry.value.toString();
        return fixed === '0' ? '' : fixed;
    }, [entry]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(e.target.value);
    }, []);

    const handleFocus = useCallback(
        (e: React.FocusEvent<HTMLInputElement, Element>) => {
            const input = e.target;
            setIsEditing(true);

            // Show raw number without trailing zeros for easier editing
            // const num = Number(entry.value.toFixed(2));
            const num = entry.value.toNumber();
            setLocalValue(num === 0 ? '' : String(num));

            // Defer select() so it runs after React re-renders with the new value
            requestAnimationFrame(() => {
                input.select();
            });
        },
        [entry],
    );

    const handleBlur = useCallback(() => {
        setIsEditing(false);

        const numericValue = Number(localValue);

        const resolvedValue =
            localValue === '' || Number.isNaN(numericValue) || numericValue <= 0 ? bigZero : roundToStep(numericValue, timeEntryRoundingInterval ?? 0);

        if (!resolvedValue.eq(entry.value)) {
            upsertEntry({
                userPeriodId: periodDataId,
                taskId,
                date,
                value: resolvedValue,
                workTypeId: workType,
                flagged: entry?.flagged ?? false,
                comment: entry?.comment ?? '',
            });
            registerTimeHours([
                {
                    userPeriodId: periodDataId,
                    taskId,
                    date,
                    value: Number(resolvedValue.toString()),
                    workTypeId: resolveWorkTypeForApi(workType),
                    flagged: entry?.flagged ?? false,
                    comment: entry?.comment ?? '',
                },
            ]);
        }
    }, [
        localValue,
        timeEntryRoundingInterval,
        entry.value,
        entry?.flagged,
        entry?.comment,
        upsertEntry,
        periodDataId,
        taskId,
        date,
        workType,
        registerTimeHours,
    ]);

    const handleFlagToggle = useCallback(() => {
        const currentValue = entry?.value ?? bigZero;
        const newFlagged = !(entry?.flagged ?? false);

        upsertEntry({
            userPeriodId: periodDataId,
            taskId,
            date,
            value: currentValue,
            workTypeId: workType,
            flagged: newFlagged,
            comment: entry?.comment ?? '',
        });

        registerTimeHours([
            {
                userPeriodId: periodDataId,
                taskId,
                date,
                value: Number(currentValue.toString()),
                workTypeId: resolveWorkTypeForApi(workType),
                flagged: newFlagged,
                comment: entry?.comment ?? '',
            },
        ]);
    }, [entry, taskId, date, workType, periodDataId, upsertEntry, registerTimeHours]);

    const handleCommentSave = useCallback(
        (comment: string) => {
            const currentValue = entry?.value ?? bigZero;

            const updatedEntry = {
                userPeriodId: periodDataId,
                taskId,
                date,
                value: currentValue,
                workTypeId: workType,
                flagged: entry?.flagged ?? false,
                comment,
            };

            upsertEntry(updatedEntry);

            registerTimeHours([
                {
                    userPeriodId: periodDataId,
                    taskId,
                    date,
                    value: Number(currentValue.toString()),
                    workTypeId: resolveWorkTypeForApi(workType),
                    flagged: entry?.flagged ?? false,
                    comment,
                },
            ]);
        },
        [entry, taskId, date, workType, periodDataId, upsertEntry, registerTimeHours],
    );

    return (
        <div className={mergeClasses(styles.cell, styles.cellHours, styles.cellEditable, dateColumnStyles[date])}>
            <input
                className={mergeClasses(styles.hoursInput, readOnly && styles.hoursInputReadOnly)}
                value={isEditing ? localValue : storeDisplayValue}
                type="text"
                aria-label={`Hours for ${taskName} on ${date}`}
                data-task-id={taskId}
                data-work-type={workType}
                data-date={date}
                readOnly={readOnly}
                tabIndex={readOnly ? -1 : undefined}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
            />
            {!readOnly && allowCellFlag && <FlagButton flagged={entry?.flagged ?? false} onToggle={handleFlagToggle} />}
            {!readOnly && allowCellComment && <CommentPopover comment={entry?.comment} onSave={handleCommentSave} />}
        </div>
    );
});
